"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { REFERENCE_FX_CURRENCY_CODES_SORTED } from "@/config/currencies";
import type { Locale } from "@/config/locale";
import { getCurrencyNarrowSymbol } from "@/lib/currency-display";
import { resolveReferenceCurrencyClient, setGeoPrefCookie } from "@/lib/geo/geo-pref-client";
import { getReferenceCurrencyForContext } from "@/lib/pricing/reference-currency-policy";
import { cn } from "@/lib/utils";

/** Locale-independent (see `compareCurrencyCodes`) — a browser-collated order would break hydration. */
const sortedCodes: readonly string[] = REFERENCE_FX_CURRENCY_CODES_SORTED;

/** Matches LanguageSelect/ChannelSelect pills in `FooterLocaleRegionControls`. */
const footerPillSelectClass =
	"h-11 min-h-[44px] w-full min-w-0 flex-1 cursor-pointer appearance-none border-0 bg-transparent py-0 pl-1.5 pr-7 text-[13px] font-medium leading-none text-foreground " +
	"rounded-none focus:outline-none focus:ring-0 focus:ring-offset-0 " +
	"focus-visible:outline-none focus-visible:ring-0";

type ReferenceCurrencySelectProps = {
	locale: Locale;
	channel: string;
	countryCode?: string | null;
	className?: string;
	id?: string;
	"aria-label"?: string;
	/** Pill layout aligned with footer language/region selectors. */
	variant?: "default" | "footer";
};

/**
 * Native `<select>` for footer — good mobile UX (OS picker).
 */
export function ReferenceCurrencySelect({
	locale,
	channel,
	countryCode = null,
	className,
	id,
	"aria-label": ariaLabel,
	variant = "default",
}: ReferenceCurrencySelectProps) {
	const router = useRouter();
	const [value, setValue] = useState(() => getReferenceCurrencyForContext({ countryCode, locale, channel }));

	useEffect(() => {
		// eslint-disable-next-line react-hooks/set-state-in-effect -- post-mount cookie correction (geo_pref_v2): lazy-initialising from the cookie would desync SSR/CSR markup; one extra render only when an override cookie exists
		setValue(resolveReferenceCurrencyClient(locale, channel, countryCode));
	}, [locale, channel, countryCode]);

	const narrowSymbol = getCurrencyNarrowSymbol(value, locale);

	const selectEl = (
		<select
			id={id}
			aria-label={ariaLabel}
			className={cn(
				variant === "footer"
					? footerPillSelectClass
					: "h-11 min-h-[44px] w-full min-w-0 cursor-pointer rounded-md border border-input bg-background px-3 py-2 text-[13px] font-medium text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
				className,
			)}
			value={value}
			onChange={(e) => {
				const code = e.target.value;
				setValue(code);
				setGeoPrefCookie({ locale, channel, referenceCurrency: code });
				router.refresh();
			}}
		>
			{sortedCodes.map((code) => {
				const sym = getCurrencyNarrowSymbol(code, locale);
				return (
					<option key={code} value={code}>
						{code} · {sym}
					</option>
				);
			})}
		</select>
	);

	if (variant === "footer") {
		return (
			<div className="border-border/60 flex min-h-[44px] min-w-0 flex-1 items-stretch overflow-hidden rounded-lg border bg-background transition-colors hover:border-border sm:max-w-[15rem]">
				<div className="border-border/60 bg-muted/25 flex min-w-[2.25rem] shrink-0 items-center justify-center border-r px-2.5">
					<span className="text-[13px] font-semibold tabular-nums text-foreground" aria-hidden>
						{narrowSymbol}
					</span>
				</div>
				{selectEl}
			</div>
		);
	}

	return selectEl;
}
